import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { AxiosError } from "axios";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { jwtDecode } from "jwt-decode";
import { useEffect } from "react";

import apiClient, {
  setAccessTokenCallback as deprecatedSetAccessTokenCallback,
} from "@utils/api";
import { setAccessTokenCallback } from "@utils/axiosMutator";

import {
  type JwtExtendedPayload,
  MockUserData,
  UserData,
  UserPermissions,
} from "@store/PermissionsAtom";

export function isDevelopmentOrLocal(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname.includes("localhost") ||
    hostname.includes("127.0.0.1") ||
    hostname.includes(".dev")
  );
}

export function useUserData() {
  const [userData, setUserData] = useAtom(UserData);
  const mockUserData = useAtomValue(MockUserData);
  const setUserPermissions = useSetAtom(UserPermissions);
  const {
    user,
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  // Handle mock user data for development/local environments
  useEffect(() => {
    if (isDevelopmentOrLocal() && mockUserData) {
      setUserData({
        employeeId: mockUserData.employeeId,
        email: mockUserData.email,
        name: mockUserData.name,
        pinnedItems: mockUserData.pinnedItems,
      });
    } else if (!mockUserData && !user) {
      setUserData(null);
    }
  }, [mockUserData, user, setUserData]);

  // Handle Auth0 authentication and user data
  useEffect(() => {
    // Skip processing if still loading, not authenticated, or no user
    if (isLoading || !isAuthenticated || !user) {
      return;
    }

    // Skip Auth0 processing if we're in mock mode
    if (isDevelopmentOrLocal() && mockUserData) {
      return;
    }

    // set datadog user info
    datadogRum.setUser({
      id: user.employee_id || user.sub,
      name: user.name,
      email: user.email,
    });

    const controller = new AbortController();
    const { signal } = controller;

    // Set up access token callback for API requests
    setupAccessTokenCallback(getAccessTokenSilently, loginWithRedirect);

    // Process user data if not already set
    if (!userData) {
      const userId = getUserId(user);
      const username = prepareUsername(user);

      // Fetch user data from backend
      fetchAndSetUserData(userId, user, username, signal);
    }

    return () => controller.abort();
  }, [
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    getAccessTokenSilently,
    mockUserData,
  ]);

  const setupAccessTokenCallback = (
    getAccessTokenSilently: any,
    loginWithRedirect: any,
  ) => {
    setAccessTokenCallback(async () => {
      const audience: string = import.meta.env.VITE_PUBLIC_AUTH0_AUDIENCE;
      const scope: string = "openid profile email offline_access";
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience, scope },
        });

        // Record the permissions on app load
        const decodedTokenPayload: JwtExtendedPayload = jwtDecode(token);
        setUserPermissions(decodedTokenPayload.permissions ?? []);
        return token;
      } catch (e) {
        if (
          e.error === "missing_refresh_token" ||
          e.error === "invalid_grant" ||
          e.error === "login_required" ||
          e.error === "consent_required"
        ) {
          const loc = window.location;
          await loginWithRedirect({
            appState: {
              returnTo: loc.pathname + loc.search + loc.hash,
            },
          });
        } else {
          throw e;
        }
      }
      return "";
    });
    deprecatedSetAccessTokenCallback(async () => {
      const audience: string = import.meta.env.VITE_PUBLIC_AUTH0_AUDIENCE;
      const scope: string = "openid profile email offline_access";
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience, scope },
        });

        // Record the permissions on app load
        const decodedTokenPayload: JwtExtendedPayload = jwtDecode(token);
        setUserPermissions(decodedTokenPayload.permissions ?? []);
        return token;
      } catch (e) {
        if (
          e.error === "missing_refresh_token" ||
          e.error === "invalid_grant" ||
          e.error === "login_required" ||
          e.error === "consent_required"
        ) {
          const loc = window.location;
          await loginWithRedirect({
            appState: {
              returnTo: loc.pathname + loc.search + loc.hash,
            },
          });
        } else {
          throw e;
        }
      }
      return "";
    });
  };

  const prepareUsername = (user: any) => {
    let username = user.name;
    if (!username && user.given_name && user.family_name) {
      username = `${user.given_name} ${user.family_name}`;
    }
    return username || user.nickname || undefined;
  };

  const handleUserNotFound = (userId: string) => {
    console.warn(`User ${userId} not found in system. Access denied.`);
    setUserData(null);
    // Only redirect if not already on the unauthorized page
    if (window.location.pathname !== "/unauthorized") {
      window.location.href = "/unauthorized";
    }
  };

  const fetchAndSetUserData = async (
    userId: string,
    user: any,
    username: string,
    signal: AbortSignal,
  ) => {
    try {
      const res = await apiClient.userControllerGetById(userId);
      if (!signal.aborted) {
        setUserData({
          ...res.data,
          email: user.email,
          name: username,
        });
      }
    } catch (e: AxiosError) {
      if (signal.aborted) return;

      if (e.response?.status === 404) {
        handleUserNotFound(userId);
      } else {
        console.error("Error fetching user data:", e);
      }
    }
  };
}

function getUserId(user: any) {
  // use employee_id if available
  if (user.employee_id) {
    return user.employee_id;
  }
  // Otherwise extract from Auth0 sub
  return user?.sub.split("|")[2];
}
